/**
 * Pure transforms behind the PV 5D dashboard: one saved Input Data revision
 * (inputData[rev] from project/component/:id/PV/all) -> the reference's single
 * row schema, plus the roll-up maths. No React, no fetching.
 *
 * Row schema (same at every level):
 *   code (stable internal key), displayCode, desc, entity, uid, unit,
 *   eq/er (est qty/rate), bq/br (budget), aq/ac (actual qty/cost),
 *   ip (invoice %), ia (invoiced $), pr/pc (paid rate/cost), sw (status)
 *
 * Estimate comes from the WBS summary the Estimation tab saves as
 * `estimation.DataGridView8`. Actual / Paid on the procurement lines come from
 * plant MRQ/PO/payment records joined by WBS code (procurementLink.js); Invoiced
 * is per discipline in the plant MIS, so it lives on the category rows, not on
 * lines. Budget has no source wired and stays null rather than being guessed.
 */

import { CATEGORIES, LINE_DEFS, displayCode } from "./wbsConfig";

export const catKeyOf = (line) => line.code.split(".")[0];

export const categoryOf = (line) =>
  CATEGORIES.find((cat) => cat.k === catKeyOf(line));

// The newest saved revision among inputData's keys (numeric ones, as PVContext
// keeps them), or null when there are none. Used when a link names no revision.
export const latestRevision = (inputData) => {
  const keys = Object.keys(inputData && typeof inputData === "object" ? inputData : {})
    .filter((key) => key.trim() !== "" && !Number.isNaN(Number(key)))
    .sort((a, b) => Number(a) - Number(b));
  return keys.length > 0 ? keys[keys.length - 1] : null;
};

// The vessel tag the Design tab saved (design.dataEquip.Tag).
export const getVesselTag = (revisionEntry) =>
  String(revisionEntry?.design?.dataEquip?.Tag ?? "").trim();

/**
 * Every spelling of a WBS code an outside record (an MRQ line, a task) may use
 * for a line, lower-cased: the stable internal code ("2.1", plant-wide) and,
 * when the revision has a tag, the tag-prefixed one ("V201.25", specific to
 * this vessel). Feed to procurementLink.js's resolveWbsCode.
 */
export const lineAliases = (lines) => {
  const aliases = new Map();
  lines.forEach((line) => {
    aliases.set(line.code.toLowerCase(), { code: line.code, scoped: false });
    if (line.displayCode !== line.code) {
      aliases.set(line.displayCode.toLowerCase(), {
        code: line.code,
        scoped: true,
      });
    }
  });
  return aliases;
};

// Only lines of a PMS category (procurement, subcontract) take POs.
export const procurementAliases = (lines) =>
  lineAliases(lines.filter((line) => categoryOf(line)?.sys === "PMS"));

// Which saved input rows carry the UIDs a summary line is built from
// (EstimationTab.getTableData): shell/head rows feed 2.1, 3.1 and the painting
// area (4.2); nozzle rows feed 2.2 and 3.2.
const UID_SOURCE = {
  "2.1": "shell",
  "3.1": "shell",
  "4.2": "shell",
  "2.2": "nozzle",
  "3.2": "nozzle",
};

export const toNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// Est cost = qty x rate when both are there (what the Estimation tab derives
// on every edit), otherwise whatever cost was stored on the row.
export const estCost = (line) => {
  if (line.ecOverride != null) return line.ecOverride;
  if (line.eq != null && line.er != null) return line.eq * line.er;
  return null;
};

export const budgetCost = (line) => {
  if (line.bcOverride != null) return line.bcOverride;
  if (line.bq != null && line.br != null) return line.bq * line.br;
  return null;
};

const collectUids = (rows, key = "UID") =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => String(row?.[key] ?? "").trim())
    .filter(Boolean);

export const buildDashboardLines = (revisionEntry) => {
  const dgv8 = revisionEntry?.estimation?.DataGridView8;
  if (!Array.isArray(dgv8)) return [];

  const tag = getVesselTag(revisionEntry);
  const uids = {
    shell: collectUids(revisionEntry?.shell?.shellDesign),
    nozzle: collectUids(revisionEntry?.nozzle?.DataGridView1),
  };

  return (
    dgv8
      // The Estimation tab only generates codes 1.x-4.x; anything else has no
      // category row to sit under, so it is left out of table and totals alike.
      .filter((row) =>
        CATEGORIES.some(
          (c) => c.k === String(row?.["WBS Code"] ?? "").trim().split(".")[0],
        ),
      )
      .map((row) => {
        const code = String(row["WBS Code"]).trim();
        const cat = CATEGORIES.find((c) => c.k === code.split(".")[0]);
        const def = LINE_DEFS[code] || {};
        const eq = toNumber(row["Est Weight"]);
        const er = toNumber(row["Est Unit Rate"]);
        const stored = toNumber(row["Est Cost"]);

        return {
          code,
          displayCode: displayCode(tag, code),
          desc: def.label || String(row.Description || "").trim(),
          entity: cat.entity,
          uid: (uids[UID_SOURCE[code]] || []).join(", "),
          unit: def.unit || String(row.Unit || "").trim(),
          eq,
          er,
          // qty x rate wins when both exist; a stored cost only stands in when
          // one of them was left blank.
          ecOverride: eq != null && er != null ? null : stored,
          bq: null,
          br: null,
          bcOverride: null,
          aq: null,
          ac: null,
          ip: null,
          ia: null,
          pr: null,
          pc: null,
          sw: null,
        };
      })
  );
};

// Puts linked procurement onto the lines it belongs to. `linked` is
// linkProcurement()'s result (null while procurement is loading or failed).
export const applyProcurement = (lines, linked) => {
  if (!linked) return lines;
  return lines.map((line) => {
    const hit = linked.byCode.get(line.code);
    return hit ? { ...line, ac: hit.ac, pc: hit.pc } : line;
  });
};

const AMOUNT_KEYS = ["est", "bud", "act", "inv", "pd"];

const emptyTotals = () => ({
  est: 0,
  bud: 0,
  act: 0,
  inv: 0,
  pd: 0,
  has: { est: false, bud: false, act: false, inv: false, pd: false },
});

const addTo = (totals, key, value) => {
  if (value == null) return;
  totals[key] += value;
  totals.has[key] = true;
};

/**
 * Totals per amount, per category and overall, plus whether anything actually
 * fed each one - so a column with no source shows "-" instead of a misleading
 * $0. `invoices` is extractInvoices()'s result; each discipline's invoiced
 * amount is added to its category, and so is `extraInvoiced` (manual invoice
 * entries, by category key).
 */
export const aggregate = (lines, invoices = {}, extraInvoiced = {}) => {
  const byCat = {};
  CATEGORIES.forEach((cat) => {
    byCat[cat.k] = emptyTotals();
  });

  lines.forEach((line) => {
    const t = byCat[catKeyOf(line)];
    if (!t) return;
    addTo(t, "est", estCost(line));
    addTo(t, "bud", budgetCost(line));
    addTo(t, "act", line.ac);
    addTo(t, "inv", line.ia);
    addTo(t, "pd", line.pc);
  });

  CATEGORIES.forEach((cat) => {
    addTo(byCat[cat.k], "inv", invoices[cat.discipline]?.amount);
    addTo(byCat[cat.k], "inv", extraInvoiced[cat.k]);
  });

  const total = emptyTotals();
  Object.values(byCat).forEach((t) => {
    AMOUNT_KEYS.forEach((key) => {
      if (t.has[key]) addTo(total, key, t[key]);
    });
  });

  return { ...total, byCat };
};

/**
 * Variances compare like with like: only categories where BOTH sides have data
 * count (procurement actuals are not measured against an all-category
 * estimate). Each result carries the categories it covered, or is null.
 */
export const variance = (totals) => {
  const pair = (a, b) => {
    const scope = CATEGORIES.filter(
      (cat) => totals.byCat[cat.k].has[a] && totals.byCat[cat.k].has[b],
    );
    if (scope.length === 0) return null;
    return {
      value: scope.reduce(
        (sum, cat) => sum + totals.byCat[cat.k][a] - totals.byCat[cat.k][b],
        0,
      ),
      scope: scope.map((cat) => cat.code),
    };
  };

  return {
    accuracy: pair("est", "act"),
    budgetPerformance: pair("bud", "act"),
    margin: pair("inv", "act"),
    receivable: pair("inv", "pd"),
  };
};
